import React, { FC, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";

import { CheckIcon, CopyIcon } from "application/assets";
import { useAppDispatch, useAppSelector } from "application/store";
import { requestSelector } from "application/store/request/selectors";
import {
  downloadDealBackground,
  getDealReferralLink,
} from "application/store/Deal/actions";
import { isResponseFulfilled } from "application/helpers/responseHelper";
import { LogoType } from "integration/api/Deal/models";

import Loader from "../Loader";
import MobilePopUp from "../MobilePopUp";

import { SharePopUpProps } from "./models";
import {
  Content,
  DealInfo,
  ConditionsList,
  BottomSection,
  LinkSection,
  LinkInfo,
  LinkButtons,
  QRCodeSection,
  QRCodeWrapper,
  QRCodeStyled,
  QRCodeInfo,
  ImageWrapper,
} from "./styles";

const SharePopUp: FC<SharePopUpProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { deal, shortReferralLink } = useAppSelector((state) => state.deal);
  const { user } = useAppSelector((state) => state.auth);
  const { loading } = useAppSelector(requestSelector).getDealReferralLink;

  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [showQrCodeModal, setShowQrCodeModal] = useState(false);
  const [dealImage, setDealImage] = useState("");

  const title = deal?.title || "";
  const qrcodeLink = user?.referralQrCodeLink || "";

  const getBackgroundImageById = async () => {
    if (deal?.backgroundImageFileId) {
      setIsLoading(true);
      const response = await dispatch(
        downloadDealBackground({
          dealId: deal.id,
          fileId: deal.backgroundImageFileId,
          logoType: LogoType.THUMBNAIL,
        })
      );
      isResponseFulfilled(response, () => {
        const data = response.payload as Blob;
        const imgBlob = new Blob([data], {
          type: data.type as string,
        });
        const imgUrl = URL.createObjectURL(imgBlob);
        setDealImage(imgUrl);
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (deal) {
      dispatch(getDealReferralLink(deal.id));
      getBackgroundImageById();
    }
  }, [deal]);

  const copyTextToClipboard = async () => {
    await navigator.clipboard.writeText(shortReferralLink);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1500);
  };

  const onCopy = () => {
    copyTextToClipboard();
  };

  return (
    <>
      {showQrCodeModal ? (
        <MobilePopUp
          open={open}
          onClose={() => setShowQrCodeModal(false)}
          title="Scan the code"
        >
          <Content>
            <QRCodeSection>
              <QRCodeWrapper>
                <QRCodeStyled>
                  <QRCodeSVG
                    value={qrcodeLink}
                    bgColor={"transparent"}
                    className="qrcode-svg"
                  />
                </QRCodeStyled>
              </QRCodeWrapper>
            </QRCodeSection>
            <QRCodeInfo>
              <h2>This is your unique referral link</h2>
              <p>
                Show this code to your friend — once they join and invest,
                you’ll earn a $100 bonus.
              </p>
            </QRCodeInfo>
          </Content>
        </MobilePopUp>
      ) : (
        <MobilePopUp
          open={open}
          onClose={onClose}
          title="Earn $100 in fee savings"
        >
          <>
            {loading && <Loader />}
            <Content>
              <DealInfo>
                <ImageWrapper>
                  {isLoading && <Loader />}
                  <img src={dealImage} alt="deal-image" />
                </ImageWrapper>
                <p>Invite a friend to invest in {title} and get rewarded!</p>
              </DealInfo>
              <ConditionsList>
                <li>Send your unique link</li>
                <li>They invest</li>
                <li>
                  You get <span>$100 off</span> your next Management Fee
                </li>
              </ConditionsList>
            </Content>
            <BottomSection>
              <LinkSection>
                <LinkInfo>
                  <h2>Your link</h2>
                  <p>{shortReferralLink}</p>
                </LinkInfo>
                <LinkButtons>
                  <button
                    type="button"
                    className={`copy-button ${isCopied ? "active" : ""}`}
                    onClick={onCopy}
                  >
                    {isCopied ? <CheckIcon /> : <CopyIcon />}
                  </button>
                  {/*Temporarily hidden*/}
                  {/* <button
                    type="button"
                    onClick={() => setShowQrCodeModal(true)}
                  >
                    <ScanBarcodeIcon />
                  </button> */}
                </LinkButtons>
              </LinkSection>
            </BottomSection>
          </>
        </MobilePopUp>
      )}
    </>
  );
};

export default SharePopUp;
